import request from 'helpers/request';
import EnvVar from 'helpers/envVars';
import * as DTO from './dto';
import formatObjectToSearchParams from 'helpers/formatObjectToSearchParams';
import formatArrayToSearchParam from 'helpers/formatArrayToSearchParam';
import { Document, DocumentType } from '../utils/interfaces';

const API_GROUP_ENDPOINT = `${EnvVar('API_PAINEL_DIGITAL_BASE_URL')}/group`;
const API_DEALER_ENDPOINT = `${EnvVar('API_PAINEL_DIGITAL_BASE_URL')}/dealer`;

export async function getBillingCenterData({
  dealerId,
  endDate,
  page = 0,
  paymentMethodName,
  query,
  responsible,
  sellersIds,
  department,
  size = 0,
  startDate,
  status,
  consortium,
}: DTO.BillingCenterQuery) {
  const params = formatObjectToSearchParams({
    page,
    paymentMethodName: formatArrayToSearchParam(paymentMethodName),
    query,
    responsible: formatArrayToSearchParam(responsible),
    sellersIds: formatArrayToSearchParam(sellersIds),
    department: formatArrayToSearchParam(department),
    size,
    status,
    consortium,
  });

  const { data } = await request.get<DTO.BillingCenterApiResponse>(
    `${API_DEALER_ENDPOINT}/${dealerId}/order/invoice/v1.0/${startDate}/${endDate}${params}`
  );

  return data;
}

export async function getBillingCenterSummaryData({
  dealerId,
  endDate,
  paymentMethodName,
  query,
  responsible,
  sellersIds,
  department,
  startDate,
  status,
  consortium,
}: DTO.SummaryQuery) {
  const params = formatObjectToSearchParams({
    paymentMethodName: formatArrayToSearchParam(paymentMethodName),
    query,
    responsible: formatArrayToSearchParam(responsible),
    sellersIds: formatArrayToSearchParam(sellersIds),
    department: formatArrayToSearchParam(department),
    status,
    consortium,
  });

  const { data } = await request.get<DTO.SummaryResponse>(
    `${API_DEALER_ENDPOINT}/${dealerId}/order/invoice/v1.0/overview/${startDate}/${endDate}${params}`
  );

  return data;
}

export async function getMatrixDocuments({
  dealerId,
  proposalId,
}: DTO.DocumentsQuery): Promise<Document[]> {
  const { data } = await request.get<Document[]>(
    `${API_DEALER_ENDPOINT}/${dealerId}/proposal/order/documents/v1.0/${proposalId}/documents`
  );

  return data
    .map(document => {
      const currentDocument: Document = document;

      if (!currentDocument?.status) {
        currentDocument.status = {
          label: 'Não iniciado',
          type: DocumentType.NOT_STARTED,
        };
      }

      return currentDocument;
    })
    .filter(document => {
      const inactiveWithFiles = !document.active && document.uploaded;
      return document.active || inactiveWithFiles;
    });
}

export async function getFilterOptions({ groupId }: DTO.GetFilterOptions) {
  const { data } = await request.get<DTO.FilterOptions>(
    `${API_GROUP_ENDPOINT}/${groupId}/order/center/v1.0/options`
  );

  return data;
}

export async function getPdfDocumentsByProposalId({
  dealerId,
  documentIds,
  proposalId,
}: DTO.GetPdfDocumentsByProposalIdQuery) {
  const params = formatObjectToSearchParams({ documentIds });

  const response = await request.get(
    `${API_DEALER_ENDPOINT}/${dealerId}/proposal/order/documents/v1.0/${proposalId}/download-pdf${params}`,
    {},
    { responseType: 'blob' }
  );

  return response?.data;
}

export async function deleteDocument({
  dealerId,
  documentId,
}: DTO.DeleteDocumentQuery) {
  const response = await request.delete(
    `${API_DEALER_ENDPOINT}/${dealerId}/proposal/order/documents/v1.0/${documentId}/delete`
  );

  return response?.data;
}
